/**
 * 무기한 로딩 스피너 (WAI-ARIA). 회전하는 8-spoke 글리프.
 *
 * Owns:
 * - LoaderIcon 글리프의 회전 애니메이션과 size(sm/md/lg/xl) → 타입 스케일 토큰 매핑(`foundations/glyphSize`, ProgressRing과 공용).
 * - label 유무에 따른 a11y 모드(있으면 role=status 라이브 리전, 없으면 장식용 aria-hidden).
 *
 * Does not own:
 * - 색: prop 없이 `currentColor`를 상속한다 — 호출 컨텍스트가 text 색으로 소유(prop 정책, RFC-design-system-split).
 * - 배치/여백: 호출자가 감싸서 소유(예: 메시지 줄, 버튼 안).
 */
import styled, { keyframes } from "styled-components";

import { LoaderIcon } from "../../icons/glyphs";
import { type GlyphSize, type GlyphSizeToken, glyphSizeToken } from "../foundations/glyphSize";

export type SpinnerSize = GlyphSize;

export type SpinnerProps = {
  size?: SpinnerSize;
  // 있으면 접근성 이름(role=status). 이미 옆 텍스트가 로딩을 전달하는 경우엔 생략해 장식 처리.
  label?: string;
  className?: string;
};

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const Spin = styled.span<{ $token: GlyphSizeToken }>`
  display: inline-flex;
  width: 1em;
  height: 1em;
  font-size: ${({ theme, $token }) => theme.typography.size[$token]};
  color: inherit;
  animation: ${spin} 1.3s linear infinite;

  /* reduced-motion: 스피너는 진행 중임을 알리는 필수 피드백이라 멈추지 않고 감속한다(정지=정보 소실). */
  @media (prefers-reduced-motion: reduce) {
    animation-duration: 2.6s;
  }

  svg {
    display: block;
    width: 100%;
    height: 100%;
  }
`;

export function Spinner({ size = "md", label, className }: SpinnerProps) {
  return (
    <Spin
      $token={glyphSizeToken[size]}
      className={className}
      role={label ? "status" : undefined}
      aria-label={label || undefined}
      aria-hidden={label ? undefined : true}
    >
      <LoaderIcon aria-hidden="true" />
    </Spin>
  );
}
